/* Developer : Nithin
Date : 26-04-2026
changes: added isLoading support to keep table headers visible during data loading */
// import React from "react";

// /**
//  * DataTable component
//  * Config-driven table rendering.
//  * 
//  * @param {Array} columns - Array of column configs: { header: string|JSX, key: string, render: func, className: string }
//  * @param {Array} data - Array of data objects
//  * @param {Array} headers - Backwards compatibility for raw children approach
//  * @param {ReactNode} children - Backwards compatibility for raw children approach
//  */
// export const DataTable = ({
//   columns,
//   data,
//   headers,
//   children,
//   maxHeight = "max-h-[550px]",
//   noScroll = false,
//   tableClassName = "",
//   headerRowStyle,
//   headerCellStyle,
//   onRowClick,
//   getRowClassName,
// }) => {
//   // Backwards compatibility: If columns aren't provided, use headers
//   const renderHeaders = columns ? columns.map(col => col.header) : headers;

//   return (
//     <div className={`overflow-x-auto ${noScroll ? '' : `overflow-y-auto ${maxHeight}`} relative custom-scrollbar border border-slate-200`}>
//       <table className={`min-w-full border-collapse ${tableClassName}`}>
//         <thead className={`${noScroll ? '' : 'sticky top-0'} z-10 bg-slate-50 border-b border-slate-200`}>
//           <tr className="bg-slate-50 divide-x divide-slate-200" style={{ height: '34px', ...headerRowStyle }}>
//             {columns ? (
//               columns.map((col, i) => (
//                 <th 
//                   key={i} 
//                   className={`th bg-slate-50 px-4 text-left border-r border-slate-200 last:border-r-0 font-['Poppins'] text-[13px] font-medium text-slate-700 ${col.headerClassName || ''}`}
//                   style={{ height: '34px', ...headerCellStyle }}
//                 >
//                   {typeof col.header === 'object' ? col.header : col.header}
//                 </th>
//               ))
//             ) : (
//               renderHeaders?.map((h, i) => (
//                 <th key={i} className="th bg-slate-50 px-4 text-left border-r border-slate-200 last:border-r-0" style={{ height: '34px', ...headerCellStyle }}>
//                   {typeof h === 'object' ? (h.label || h.header || JSON.stringify(h)) : h}
//                 </th>
//               ))
//             )}
//           </tr>
//         </thead>
//         <tbody className="divide-y divide-slate-200 bg-white">
//           {columns && data ? (
//             data.length > 0 ? (
//               data.map((row, rowIndex) => (
//                 <tr 
//                   key={row._id || row.id || rowIndex} 
//                   onClick={() => onRowClick && onRowClick(row, rowIndex)}
//                   className={`hover:bg-slate-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${getRowClassName ? getRowClassName(rowIndex, row) : ''}`}
//                 >
//                   {columns.map((col, colIndex) => (
//                     <td key={colIndex} className={`td px-4 py-3 ${col.cellClassName || col.className || ""}`}>
//                       {col.render 
//                         ? col.render(row, rowIndex) 
//                         : (col.accessor 
//                             ? (typeof col.accessor === 'function' ? col.accessor(row, rowIndex) : row[col.accessor])
//                             : row[col.key]
//                           )
//                       }
//                     </td>
//                   ))}
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan={renderHeaders?.length || 1} className="p-8 text-center text-slate-500 font-medium">
//                   No records found
//                 </td>
//               </tr>
//             )
//           ) : (
//             children
//           )}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default DataTable;

import React, { useState, useMemo } from "react";
import Pagination from "./Pagination";

/**
 * DataTable component
 * Config-driven table rendering.
 * 
 * @param {Array} columns - Array of column configs: { header: string|JSX, key: string, render: func, className: string }
 * @param {Array} data - Array of data objects
 * @param {Array} headers - Backwards compatibility for raw children approach
 * @param {ReactNode} children - Backwards compatibility for raw children approach
 */
export const DataTable = ({
  columns,
  data,
  headers,
  children,
  maxHeight = "max-h-[550px]",
  noScroll = false,
  tableClassName = "",
  headerRowStyle,
  headerCellStyle,
  onRowClick,
  getRowClassName,
  getRowTitle,
  isRowInactive,
  isLoading = false,
  loadingText = "Loading...",
  backgroundImage = null,
  wrapperClassName = "border border-slate-300 rounded-lg",
  enablePagination = true,
  pageSize = 10,
  page: externalPage,
  totalPages: externalTotalPages,
  onPageChange: onExternalPageChange,
}) => {
  const [internalPage, setInternalPage] = useState(1);

  // Backwards compatibility: If columns aren't provided, use headers
  const renderHeaders = columns ? columns.map(col => col.header) : headers;

  // Determine if using external (server-side) or internal (client-side) pagination
  const isExternalPagination = typeof externalPage !== 'undefined' && typeof onExternalPageChange === 'function';
  
  const currentPage = isExternalPagination ? externalPage : internalPage;
  const totalItems = data ? data.length : 0;
  const totalPages = isExternalPagination 
    ? (externalTotalPages || 1) 
    : Math.ceil(totalItems / pageSize);

  const handlePageChange = (newPage) => {
    if (isExternalPagination) {
      onExternalPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  // Memoized current page data slice for internal pagination
  const paginatedData = useMemo(() => {
    if (!data) return [];
    if (!enablePagination || isExternalPagination) return data;
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, enablePagination, isExternalPagination, currentPage, pageSize]);

  return (
    <div className={`relative bg-white overflow-hidden ${wrapperClassName} flex flex-col`}>
      <div className={`overflow-x-auto ${noScroll ? '' : `overflow-y-auto ${maxHeight}`} relative custom-scrollbar z-10 flex-grow`}>
        <table className={`min-w-full border-collapse relative ${tableClassName}`}>
          <thead className={`${noScroll ? '' : 'sticky top-[-1px]'} z-30 bg-[#64748b] border-b border-slate-500`}>
            <tr className="divide-x divide-slate-500" style={{ height: '36px', ...headerRowStyle }}>
              {columns ? (
                columns.map((col, i) => (
                  <th
                    key={i}
                    className={`th bg-[#64748b] px-4 py-2.5 text-left font-sans text-[13px] font-semibold text-white tracking-wider whitespace-nowrap border-r border-slate-500 last:border-r-0 ${col.headerClassName || ''}`}
                    style={{ height: '36px', ...(col.minWidth ? { minWidth: col.minWidth } : {}), ...headerCellStyle }}
                  >
                    {typeof col.header === 'object' ? col.header : col.header}
                  </th>
                ))
              ) : (
                renderHeaders?.map((h, i) => (
                  <th key={i} className="th bg-[#64748b] px-4 py-2.5 text-left font-sans text-[13px] font-semibold text-white tracking-wider whitespace-nowrap border-r border-slate-500 last:border-r-0" style={{ height: '36px', ...headerCellStyle }}>
                    {typeof h === 'object' ? (h.label || h.header || JSON.stringify(h)) : h}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white font-sans">
            {isLoading ? (
              <tr>
                <td colSpan={renderHeaders?.length || 1} className="p-8 text-center text-slate-500 font-medium">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{loadingText}</span>
                  </div>
                </td>
              </tr>
            ) : columns && data ? (
              paginatedData.length > 0 ? (
                paginatedData.map((row, index) => {
                  const rowIndex = isExternalPagination ? index : ((currentPage - 1) * pageSize + index);
                  return (
                    <tr
                      id={`row-${row._id || row.id || rowIndex}`}
                      key={row._id || row.id || rowIndex}
                      title={getRowTitle ? getRowTitle(row, rowIndex) : undefined}
                      onClick={() => onRowClick && onRowClick(row, rowIndex)}
                      className={`transition-colors duration-150 divide-x divide-slate-200
                                ${isRowInactive && isRowInactive(row)
                          ? 'opacity-70 bg-slate-50'
                          : (rowIndex % 2 === 0 ? 'bg-slate-50/50' : 'bg-white')} 
                                hover:bg-slate-100
                                ${onRowClick ? 'cursor-pointer' : ''} 
                                ${getRowClassName ? getRowClassName(row, rowIndex) : ''}
                              `}
                    >
                      {columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={`td px-4 py-0.5 text-[13px] font-medium text-slate-700 ${col.cellClassName || col.className || ""}`}
                          style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                        >
                          {col.render
                            ? col.render(row, rowIndex)
                            : (col.accessor
                              ? (typeof col.accessor === 'function' ? col.accessor(row, rowIndex) : row[col.accessor])
                              : row[col.key]
                            )
                          }
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={renderHeaders?.length || 1} className="p-8 text-center text-slate-500 font-medium">
                    No records found
                  </td>
                </tr>
              )
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
      {enablePagination && totalPages > 1 && (
        <Pagination 
          page={currentPage} 
          totalPages={totalPages} 
          onPageChange={handlePageChange} 
        />
      )}
    </div>
  );
};

export default DataTable;